// ─────────────────────────────────────────────────────────────────────────────
// Hook partagé pour le profil clavier persisté (RDP + Vidéo).
//
// Avant ce hook, chaque panneau dérivait le profil d'un signal legacy
// (`cmdKeyMode` en RDP, `swapCmdCtrl` en Vidéo), ce qui empêchait le profil
// personnalisé d'être réellement chargé. Ce hook lit le profil via
// `loadProfile()` (qui supporte "custom") et expose `setActiveProfileId` qui
// persiste via `saveProfileId` / `saveCustomProfile`.
//
// Le profil est un signal réactif : quand `setActiveProfileId` change l'id, le
// memo se recalcule. Les panneaux consomment `profile()` dans `resolveKey` /
// `evaluateKey`.
// ─────────────────────────────────────────────────────────────────────────────

import { createMemo, createSignal } from "solid-js";
import { loadProfile, macProfile, windowsProfile, saveProfileId, type KeyboardProfile } from "./keyboard-profile";

export interface KeyboardProfileState {
  /** Profil actuel (réactif). */
  profile: () => KeyboardProfile;
  /** ID du profil actif ("mac" | "windows" | "custom"). */
  activeProfileId: () => string;
  /** Change le profil actif et le persiste. */
  setActiveProfileId: (id: string) => void;
}

/**
 * Crée un état de profil clavier persisté. À appeler une fois par panneau
 * (RDP, Vidéo). Le profil est partagé entre les panneaux via localStorage
 * (clé `rws.keyboard.profile`), donc changer le profil dans un panneau affecte
 * l'autre au prochain montage — c'est intentionnel (un opérateur a un profil
 * de saisie, pas un par panneau).
 */
export function useKeyboardProfile(): KeyboardProfileState {
  const initial = loadProfile();
  const [activeProfileId, setActiveProfileIdSignal] = createSignal<string>(initial.id);
  // Le memo recharge le profil depuis le storage quand l'id change — nécessaire
  // car le profil "custom" peut avoir été édité entre deux activations.
  const profile = createMemo<KeyboardProfile>(() => {
    const id = activeProfileId();
    if (id === "mac") return macProfile();
    if (id === "windows") return windowsProfile();
    // "custom" : recharge depuis le storage (pour capter une édition récente).
    return loadProfile("custom");
  });

  function setActiveProfileId(id: string): void {
    saveProfileId(id);
    setActiveProfileIdSignal(id);
  }

  return { profile, activeProfileId, setActiveProfileId };
}
